import { Box, Tabs, Tab, Switch } from "@mui/material";
import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePermission from "../../hooks/userAccess";
import { useGetStoreByIdQuery } from "../../services/store";
import SwitchToggle from "../../components/SwitchToggle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
}

const StoreDetail = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();

  const { data: storeByIdRes, isFetching: isFetchingStore } =
    useGetStoreByIdQuery(
      { id: routeId || "1" }, // Use routeId if available, otherwise default
      { skip: !routeId }
    );

  const storeData = useMemo(
    () => (storeByIdRes as any)?.data || null,
    [storeByIdRes]
  );
  const { isView } = usePermission({
    label: "Store Management",
    page_permission: "isView",
  });

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const formatTime = (minutes: number): string => {
    if (minutes === 0) return "12:00 AM";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${mins.toString().padStart(2, "0")} ${ampm}`;
  };

  return (
    <>
      <div className="main_title">
        <h1>Store Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-stores")}>
            Store Management
          </button>{" "}
          - Store Detail
        </p>
      </div>

      {isView && (
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: "100%" }}>
              <Tabs
                value={valueTabs}
                onChange={handleChangeTabs}
                aria-label="basic tabs example"
                className="custom_tabs_links"
              >
                <Tab label="General Store Info" {...a11yProps(0)} />
                <Tab label="Store Availability" {...a11yProps(1)} />
              </Tabs>
            </Box>
          </div>
        </div>
      )}

      {isView && (
        <div className="cards view_page">
          <CustomTabPanel value={valueTabs} index={0}>
            <div className="gap_p">
              <div className="vew_img v2 no_radius w_100">
                <h6>Store Image</h6>
                <figure>
                  <img alt="user" src={storeData?.image || ""} />
                </figure>
              </div>
              <div className="w_33">
                <h6>Store Code</h6>
                <p>{storeData?.storeCode || ""}</p>
              </div>
              <div className="w_33">
                <h6>Store Name</h6>
                <p>{storeData?.name || ""}</p>
              </div>
              <div className="w_33">
                <h6>Store Manager Contact</h6>
                <p>{storeData?.storeManagerContact || ""}</p>
              </div>
              <div className="w_100">
                <h6>Address</h6>
                <p>{storeData?.address || ""}</p>
              </div>
              <div className="w_33">
                <h6>Region / City</h6>
                <p>{storeData?.city || ""}</p>
              </div>
              <div className="w_33">
                <h6>Status</h6>
                <p className="c_success">
                  {storeData?.status === 1 ? "Open" : "Closed"}
                </p>
              </div>
              <div className="w_33">
                <h6>Created Date</h6>
                <p>
                  {storeData?.createdAt
                    ? new Date(storeData.createdAt).toLocaleDateString()
                    : ""}
                </p>
              </div>
              <div className="w_33">
                <h6>Order Online Link</h6>
                <p>{storeData?.orderOnlineLink || ""}</p>
              </div>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={1}>
            <div className="gap_p">
              <div className="w_100">
                {storeData?.is24_7 ? (
                  <>
                    <h6>Availability 24/7</h6>
                    <SwitchToggle
                      checked={storeData?.is24_7 || false}
                      onChange={() => { }}
                      disabled
                    />
                  </>
                ) : (
                  <>
                    <h6>Weekly Availability</h6>
                    <ul className="availability_list">
                      {storeData?.availability?.map((item: any) => (
                        <li key={item._id}>
                          <strong>{item.day}:</strong>{" "}
                          {item.isOpen
                            ? `${formatTime(item.startTime)} - ${formatTime(
                              item.endTime
                            )}`
                            : "Closed"}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </CustomTabPanel>
        </div>
      )}
    </>
  );
};

export default StoreDetail;
